import { App, appMetadata } from "@teamworksdev/react";

const isString = (value: any): value is string => typeof value === "string";
const isNumber = (value: any): value is number => typeof value === "number";

type ValueGetter<T, U> = keyof T | ((value: T) => U);

type SortOptions = {
  reverse?: boolean;
};

const getStringValue = <T>(value: T, getter?: ValueGetter<T, string>): string => {
  if (!value) return "";

  if (getter) {
    if (typeof getter === "function") {
      const result = getter(value);

      return typeof result === "string" ? result : "";
    }

    if (typeof value !== "object") return "";

    if (!isString(value?.[getter])) return "";

    return value[getter];
  }

  if (typeof value !== "string") return "";

  return value;
};

const getNumericValue = <T>(value: T, getter?: ValueGetter<T, number>): number => {
  if (!value) return 0;

  if (getter) {
    if (typeof getter === "function") {
      const result = getter(value);

      return typeof result === "number" ? result : 0;
    }

    if (typeof value !== "object") return 0;

    if (!isNumber(value?.[getter])) return 0;

    return value[getter];
  }

  return 0;
};

const sortAlphanumeric = <T>(a?: T, b?: T, getter?: ValueGetter<T, string>, options?: SortOptions): number => {
  if (!a || !b) return 0;

  const reverse = options?.reverse ?? false;

  const str1 = getStringValue(reverse ? b : a, getter);
  const str2 = getStringValue(reverse ? a : b, getter);

  return str1.toLocaleLowerCase().localeCompare(str2.toLocaleLowerCase(), undefined, { numeric: true });
};

const alphanumeric = <T>(getter?: ValueGetter<T, string>, options?: SortOptions): ((a?: T, b?: T) => number) => {
  return (a, b) => sortAlphanumeric(a, b, getter, options);
};

const sortNumeric = <T>(a?: T, b?: T, getter?: ValueGetter<T, number>, options?: SortOptions): number => {
  if (!a || !b) return 0;

  const reverse = options?.reverse ?? false;

  const n1 = getNumericValue(reverse ? b : a, getter);
  const n2 = getNumericValue(reverse ? a : b, getter);

  return n1 - n2;
};

const numeric = <T>(getter?: ValueGetter<T, number>, options?: SortOptions): ((a?: T, b?: T) => number) => {
  return (a, b) => sortNumeric(a, b, getter, options);
};

export const sortingFns = {
  alphanumeric,
  numeric,
};

// Axle and Hub first, then alphanumeric by display name — the People tab's app ordering.
export const sortApps = (a: App, b: App): number => {
  if (a === App.axle) return -1;
  if (b === App.axle) return 1;
  if (a === App.hub) return -1;
  if (b === App.hub) return 1;
  return sortingFns.alphanumeric()(appMetadata[a].name, appMetadata[b].name);
};
